import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { User, ShoppingBag, LogOut, Truck, Bell, Gift, Check, Plus, Trash2, CalendarIcon, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Pencil } from "lucide-react";

const EMPTY_INS = { insurance_name: "", insurance_id: "", bin_rx: "", pcn: "", grp_rx: "" };

function InsuranceTab() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_INS);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/insurances/my"); setList(data); } catch { /* */ } }

  function openNew() { setEditing(null); setForm(EMPTY_INS); setOpen(true); }
  function openEdit(ins) {
    setEditing(ins);
    setForm({ insurance_name: ins.insurance_name, insurance_id: ins.insurance_id, bin_rx: ins.bin_rx, pcn: ins.pcn, grp_rx: ins.grp_rx });
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault(); setLoading(true);
    try {
      if (editing) await api.put(`/insurances/${editing.id}`, form);
      else await api.post("/insurances", form);
      toast.success(editing ? "Insurance updated." : "Insurance saved.");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function remove(id) {
    try { await api.delete(`/insurances/${id}`); toast.success("Insurance removed."); load(); }
    catch { toast.error("Failed."); }
  }

  async function makePrimary(id) {
    try { await api.post(`/insurances/${id}/primary`); toast.success("Set as primary."); load(); }
    catch { toast.error("Failed."); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><ShieldCheck className="h-4 w-4 text-primary" /> Save multiple insurances and update them anytime.</div>
        <Button onClick={openNew} className="rounded-full" data-testid="ins-add"><Plus className="mr-1 h-4 w-4" /> Add Insurance</Button>
      </div>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="ins-empty">No insurances saved yet.</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {list.map((ins) => (
            <div key={ins.id} className="rounded-2xl border border-border bg-card p-5" data-testid={`ins-card-${ins.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="font-heading text-lg font-bold">{ins.insurance_name}</h3>
                  {ins.is_primary && <Badge className="bg-primary">Primary</Badge>}
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(ins)} data-testid={`ins-edit-${ins.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => remove(ins.id)} data-testid={`ins-delete-${ins.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              {!ins.is_primary && (
                <button onClick={() => makePrimary(ins.id)} data-testid={`ins-primary-${ins.id}`} className="mt-1 text-xs font-medium text-primary hover:underline">Set as primary</button>
              )}
              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <InsField label="Insurance ID" value={ins.insurance_id} />
                <InsField label="BIN (BinRx)" value={ins.bin_rx} />
                <InsField label="PCN" value={ins.pcn} />
                <InsField label="Group (GrpRx)" value={ins.grp_rx} />
              </dl>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Edit Insurance" : "Add Insurance"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2"><Label>Insurance name</Label><Input data-testid="ins-name" value={form.insurance_name} onChange={set("insurance_name")} required /></div>
            <div className="space-y-2"><Label>Insurance ID</Label><Input data-testid="ins-id" value={form.insurance_id} onChange={set("insurance_id")} required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>BIN (BinRx)</Label><Input data-testid="ins-bin" value={form.bin_rx} onChange={set("bin_rx")} required /></div>
              <div className="space-y-2"><Label>PCN</Label><Input data-testid="ins-pcn" value={form.pcn} onChange={set("pcn")} required /></div>
            </div>
            <div className="space-y-2"><Label>Group (GrpRx)</Label><Input data-testid="ins-grp" value={form.grp_rx} onChange={set("grp_rx")} required /></div>
            <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="ins-save">{loading ? "Saving..." : editing ? "Update Insurance" : "Save Insurance"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InsField({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value || "—"}</dd>
    </div>
  );
}

function AuthForms() {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [login_, setLogin] = useState({ email: "", password: "" });
  const [reg, setReg] = useState({ name: "", email: "", phone: "", address: "", password: "", allergies: "", diagnoses: "", date_of_birth: "" });
  const [loading, setLoading] = useState(false);

  async function doLogin(e) {
    e.preventDefault(); setLoading(true);
    const res = await login(login_.email, login_.password);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
    else if (res.user.role === "admin") { toast.success("Welcome, admin"); }
    else toast.success("Welcome back!");
  }
  async function doReg(e) {
    e.preventDefault(); setLoading(true);
    const res = await register(reg);
    setLoading(false);
    if (!res.ok) toast.error(res.error);
    else toast.success("Account created! Welcome to Crystal Pharmacy.");
  }

  return (
    <div className="mx-auto max-w-md px-6 py-20">
      <div className="mb-8 text-center">
        <h1 className="font-heading text-3xl font-bold tracking-tight">Patient Portal</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in or create an account to manage your care.</p>
      </div>
      <Tabs value={mode} onValueChange={setMode} className="rounded-2xl border border-border bg-card p-6">
        <TabsList className="mb-6 grid w-full grid-cols-2">
          <TabsTrigger value="login" data-testid="portal-tab-login">Log In</TabsTrigger>
          <TabsTrigger value="signup" data-testid="portal-tab-signup">Sign Up</TabsTrigger>
        </TabsList>
        <TabsContent value="login">
          <form onSubmit={doLogin} className="space-y-4">
            <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="login-email" value={login_.email} onChange={(e) => setLogin({ ...login_, email: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="login-password" value={login_.password} onChange={(e) => setLogin({ ...login_, password: e.target.value })} required /></div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-full">{loading ? "..." : "Log In"}</Button>
          </form>
        </TabsContent>
        <TabsContent value="signup">
          <form onSubmit={doReg} className="space-y-4">
            <div className="space-y-2"><Label>Full name</Label><Input data-testid="signup-name" value={reg.name} onChange={(e) => setReg({ ...reg, name: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" data-testid="signup-email" value={reg.email} onChange={(e) => setReg({ ...reg, email: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Phone</Label><Input data-testid="signup-phone" value={reg.phone} onChange={(e) => setReg({ ...reg, phone: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Date of birth</Label><Input type="date" data-testid="signup-dob" value={reg.date_of_birth} onChange={(e) => setReg({ ...reg, date_of_birth: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Address</Label><Input data-testid="signup-address" value={reg.address} onChange={(e) => setReg({ ...reg, address: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Allergies <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label><Textarea data-testid="signup-allergies" placeholder="e.g. Penicillin, sulfa drugs, latex — or 'None'" value={reg.allergies} onChange={(e) => setReg({ ...reg, allergies: e.target.value })} /></div>
            <div className="space-y-2"><Label>Diagnoses / conditions <span className="text-xs font-normal text-muted-foreground">(optional)</span></Label><Textarea data-testid="signup-diagnoses" placeholder="e.g. Type 2 diabetes, hypertension" value={reg.diagnoses} onChange={(e) => setReg({ ...reg, diagnoses: e.target.value })} /></div>
            <div className="space-y-2"><Label>Password</Label><Input type="password" data-testid="signup-password" value={reg.password} onChange={(e) => setReg({ ...reg, password: e.target.value })} required /></div>
            <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full rounded-full">{loading ? "..." : "Create Account"}</Button>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Dashboard() {
  const { user, logout } = useAuth();
  return (
    <div className="mx-auto max-w-4xl px-6 py-16">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Welcome, {user.name?.split(" ")[0]}</h1>
          <p className="text-sm text-muted-foreground">Manage your Crystal Pharmacy profile, orders, deliveries and reminders.</p>
        </div>
        <Button variant="outline" onClick={logout} className="rounded-full" data-testid="portal-logout"><LogOut className="mr-2 h-4 w-4" /> Log Out</Button>
      </div>

      <LoyaltyBadge />

      <Tabs defaultValue="profile" className="mt-6">
        <TabsList className="mb-6 flex flex-wrap">
          <TabsTrigger value="profile" data-testid="portal-dash-profile">My Profile</TabsTrigger>
          <TabsTrigger value="ordering" data-testid="portal-dash-ordering">Ordering Profile</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="portal-dash-deliveries">Delivery History</TabsTrigger>
          <TabsTrigger value="insurance" data-testid="portal-dash-insurance">Insurance</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="portal-dash-reminders">Refill Reminders</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="grid gap-4 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2">
            <Field label="Name" value={user.name} />
            <Field label="Email" value={user.email} />
            <Field label="Phone" value={user.phone} />
            <Field label="Date of birth" value={user.date_of_birth} />
            <Field label="Address" value={user.address} />
            <Field label="Allergies" value={user.allergies} />
            <Field label="Diagnoses / conditions" value={user.diagnoses} />
          </div>
        </TabsContent>

        <TabsContent value="ordering">
          <div className="rounded-2xl border border-border bg-card p-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary text-primary"><ShoppingBag className="h-7 w-7" /></div>
            <h3 className="font-heading text-xl font-bold">Order an OTC product</h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Send a request for a new over-the-counter item to be reserved for pickup or delivered to your home.</p>
            <Button asChild className="mt-5 rounded-full px-8" data-testid="portal-goto-otc"><Link to="/otc-store">Browse & Reserve OTC Items</Link></Button>
          </div>
        </TabsContent>

        <TabsContent value="deliveries"><DeliveryHistoryTab /></TabsContent>
        <TabsContent value="insurance"><InsuranceTab /></TabsContent>
        <TabsContent value="reminders"><RefillRemindersTab user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

function LoyaltyBadge() {
  const [loyalty, setLoyalty] = useState(null);
  useEffect(() => { api.get("/loyalty/me").then(({ data }) => setLoyalty(data)).catch(() => {}); }, []);
  if (!loyalty) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/30 bg-secondary/60 p-5" data-testid="portal-loyalty">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-white"><Gift className="h-6 w-6" /></div>
        <div>
          <p className="font-heading text-lg font-bold">{loyalty.points} loyalty points</p>
          <p className="text-sm text-muted-foreground">Earn 1 point per OTC reservation. 10 points = $5 off.</p>
        </div>
      </div>
      {loyalty.reward_available
        ? <Badge className="bg-primary text-white">$5 reward ready!</Badge>
        : <Badge variant="outline">{loyalty.points_to_reward} pts to your next $5 reward</Badge>}
    </div>
  );
}

function DeliveryHistoryTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/delivery/my").then(({ data }) => setItems(data)).catch(() => {}); }, []);
  if (items.length === 0)
    return <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="portal-delivery-empty">No delivery requests yet.</div>;
  return (
    <div className="space-y-3">
      {items.map((d) => (
        <div key={d.id} className="rounded-xl border border-border/60 bg-card p-5" data-testid={`portal-delivery-${d.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm"><span className="font-medium">Rx:</span> {d.rx_numbers}</p>
              <p className="text-sm text-muted-foreground">{d.address}</p>
              <p className="mt-1 text-xs text-muted-foreground">Requested {format(new Date(d.created_at), "MMM d, yyyy h:mm a")}</p>
              {d.delivered && <p className="mt-1 flex items-center gap-1 text-xs font-medium text-primary"><Check className="h-3 w-3" /> Delivered {d.delivered_at ? format(new Date(d.delivered_at), "MMM d, yyyy h:mm a") : ""}</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              {d.delivered ? <Badge className="bg-primary">Delivered</Badge> : <Badge className="bg-orange-500">In progress</Badge>}
              {d.signature && (
                <div className="text-right">
                  <p className="mb-1 flex items-center justify-end gap-1 text-xs text-muted-foreground"><PenLine className="h-3 w-3" /> Signed confirmation</p>
                  <img src={d.signature} alt="signature" className="h-14 rounded border border-border bg-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function RefillRemindersTab({ user }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ medication: "", rx_number: "", supply_days: "30", fill_date: null });
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/refill-reminders/my"); setList(data); } catch { /* */ } }

  async function submit(e) {
    e.preventDefault();
    if (!form.fill_date) return toast.error("Pick the date you filled/will fill it.");
    setLoading(true);
    try {
      await api.post("/refill-reminders", {
        medication: form.medication, rx_number: form.rx_number || null,
        supply_days: Number(form.supply_days), fill_date: format(form.fill_date, "yyyy-MM-dd"),
      });
      toast.success("Refill reminder set! We'll notify you by email and text when it's due.");
      setForm({ medication: "", rx_number: "", supply_days: "30", fill_date: null }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  async function cancel(id) {
    try { await api.delete(`/refill-reminders/${id}`); toast.success("Reminder cancelled."); load(); }
    catch { toast.error("Failed."); }
  }

  return (
    <div className="space-y-6">
      <form onSubmit={submit} className="grid gap-3 rounded-2xl border border-border bg-card p-6 sm:grid-cols-2">
        <div className="space-y-2"><Label>Medication</Label><Input data-testid="refill-medication" value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} required /></div>
        <div className="space-y-2"><Label>Rx number (optional)</Label><Input data-testid="refill-rx" value={form.rx_number} onChange={(e) => setForm({ ...form, rx_number: e.target.value })} /></div>
        <div className="space-y-2">
          <Label>Supply length</Label>
          <Select value={form.supply_days} onValueChange={(v) => setForm({ ...form, supply_days: v })}>
            <SelectTrigger data-testid="refill-supply"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="30">30-day supply</SelectItem><SelectItem value="90">90-day supply</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Date filled</Label>
          <Popover><PopoverTrigger asChild><Button type="button" variant="outline" data-testid="refill-date" className="w-full justify-start font-normal"><CalendarIcon className="mr-2 h-4 w-4" />{form.fill_date ? format(form.fill_date, "PP") : "Pick date"}</Button></PopoverTrigger>
            <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={form.fill_date} onSelect={(d) => setForm({ ...form, fill_date: d })} initialFocus /></PopoverContent></Popover>
        </div>
        <div className="sm:col-span-2 flex items-center gap-2 rounded-lg bg-secondary p-3 text-xs text-secondary-foreground">
          <Bell className="h-4 w-4 text-primary" /> We'll remind you {form.supply_days} days after the fill date, then every {form.supply_days} days.
        </div>
        <div className="sm:col-span-2"><Button type="submit" disabled={loading} data-testid="refill-submit" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Set Reminder</Button></div>
      </form>

      {list.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground" data-testid="refill-empty">No refill reminders yet.</div>
      ) : (
        <div className="space-y-3">
          {list.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border/60 bg-card p-5" data-testid={`refill-item-${r.id}`}>
              <div>
                <p className="font-heading font-bold">{r.medication} <span className="ml-2 text-xs font-normal text-muted-foreground">{r.supply_days}-day</span></p>
                <p className="text-sm text-muted-foreground">Next reminder: {format(new Date(r.next_due), "MMM d, yyyy")}{r.rx_number ? ` • Rx ${r.rx_number}` : ""}</p>
              </div>
              <div className="flex items-center gap-2">
                {r.active ? <Badge className="bg-primary">Active</Badge> : <Badge variant="outline">Cancelled</Badge>}
                {r.active && <Button size="sm" variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => cancel(r.id)} data-testid={`refill-cancel-${r.id}`}><Trash2 className="mr-1 h-3 w-3" /> Cancel</Button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium">{value || "—"}</p>
    </div>
  );
}

export default function PatientPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (user === null) return null;
  if (user && user.role === "admin") { navigate("/admin"); return null; }
  return user ? <Dashboard /> : <AuthForms />;
}
